// Section 4 phase 2 (Decide): applies the Decision logged for this season. step() has already
// validated it (validateDecision) and appended it to decisionLog. Policy changes take effect
// for this season and may carry a one-time trust cost (Section 18). Then the season's standing
// allocations are drawn: beds by household (Section 9) and tools (Section 10).
import { RESOURCES } from '../core/constants';
import { mintId } from '../core/ids';
import { clamp } from '../core/math';
import { peopleSorted } from '../core/queries';
import { sortedIds } from '../core/sorted';
import type {
  BuildCommand,
  Decision,
  Phase,
  PhaseContext,
  PolicyId,
  Policies,
  Resource,
} from '../core/types';
import { assignHousing } from './shared/housing';
import { addStock } from './shared/stocks';
import { allocateTools } from './shared/tools';
import { num } from './shared/value';

function setPolicy<K extends PolicyId>(ctx: PhaseContext, id: K, value: Policies[K]): void {
  const from = ctx.draft.policies[id];
  if (JSON.stringify(from) === JSON.stringify(value)) return;
  const def = ctx.registry.policies[id] as unknown as {
    changeCost: (a: Policies[K], b: Policies[K]) => number;
  };
  const cost = def.changeCost(from, value);
  ctx.draft.policies[id] = value;
  if (cost !== 0) {
    for (const p of peopleSorted(ctx.draft))
      p.trust = clamp(p.trust + cost, 0, ctx.cfg.people.statMax);
  }
  // The Ledger's policyChanges row is written from this entry (one writer, ADR-0015).
  ctx.report.add({ kind: 'policy-changed', policyId: id, trustCost: cost });
}

function applyBuild(ctx: PhaseContext, cmd: BuildCommand): void {
  const queue = ctx.draft.buildQueue;
  switch (cmd.kind) {
    case 'queue':
      queue.push({
        id: mintId(ctx.draft, 'order'),
        buildingType: cmd.buildingType,
        pointsPaid: 0,
        pointsRequired: num(ctx, { kind: 'buildCost', buildingType: cmd.buildingType }),
        materialsPaid: false,
        paused: false,
        queuedTurn: ctx.season.turn,
        repairOf: cmd.repairOf,
      });
      return;
    case 'cancel': {
      const order = queue.find((o) => o.id === cmd.orderId);
      if (order === undefined) return;
      const refunded: Partial<Record<Resource, number>> = {};
      if (order.materialsPaid) {
        // Section 9 cancel: a share of what the order actually paid (a repair paid a share of
        // the materials); saves from before M6 did not record it, so fall back to the full cost.
        const paid = order.materialsSpent ?? ctx.registry.buildings[order.buildingType].materials;
        for (const r of RESOURCES) {
          const amount = paid[r] ?? 0;
          if (amount <= 0) continue;
          const back = amount * ctx.cfg.building.cancelRefund;
          addStock(ctx, r, back, 'other');
          refunded[r] = back;
        }
      }
      ctx.draft.buildQueue = queue.filter((o) => o.id !== cmd.orderId);
      ctx.report.add({
        kind: 'build-cancelled',
        orderId: order.id,
        buildingType: order.buildingType,
        refunded,
      });
      return;
    }
    case 'pause': {
      const order = queue.find((o) => o.id === cmd.orderId);
      if (order !== undefined) order.paused = cmd.paused;
      return;
    }
    case 'move': {
      const from = queue.findIndex((o) => o.id === cmd.orderId);
      if (from < 0) return;
      const [order] = queue.splice(from, 1);
      if (order !== undefined) queue.splice(clamp(cmd.toIndex, 0, queue.length), 0, order);
      return;
    }
  }
}

function applyDecision(ctx: PhaseContext, decision: Decision): void {
  const d = ctx.draft;
  for (const id of sortedIds(decision.assignments)) {
    const p = d.people[id];
    const job = decision.assignments[id];
    if (p === undefined || job === undefined || p.job === job) continue;
    p.job = job;
    p.streaks.inJob = 0;
  }
  for (const id of Object.keys(decision.policies).sort() as PolicyId[]) {
    const value = decision.policies[id];
    if (value !== undefined) setPolicy(ctx, id, value);
  }
  for (const [resource, overrides] of Object.entries(decision.rations)) {
    d.rationOverrides[resource as keyof typeof d.rationOverrides] = overrides;
  }
  for (const cmd of decision.builds) applyBuild(ctx, cmd);
  for (const id of sortedIds(decision.toolOverrides))
    d.toolOverrides[id] = decision.toolOverrides[id] ?? false;
  for (const id of sortedIds(decision.triageOverrides))
    d.triageOverrides[id] = decision.triageOverrides[id] ?? false;
  for (const id of sortedIds(decision.apprenticeships)) {
    const p = d.people[id];
    if (p !== undefined) p.masterId = decision.apprenticeships[id] ?? null;
  }
  if (decision.savedPriorityOrders !== null) d.savedPriorityOrders = decision.savedPriorityOrders;
  if (decision.myHousehold !== null) d.myHousehold = decision.myHousehold.slice().sort();
  for (const id of sortedIds(decision.renames)) {
    const p = d.people[id];
    const name = decision.renames[id];
    if (p !== undefined && name !== undefined) p.name = name;
  }
  d.promptAnswers = { ...d.promptAnswers, ...decision.eventChoices, ...decision.promptAnswers };
}

/** Section 9: beds are assigned by household at the start of the season (housing policy). */
function house(ctx: PhaseContext): void {
  const { assignment, crowding } = assignHousing(ctx.state, ctx.registry);
  let rough = 0;
  for (const id of sortedIds(assignment)) {
    const current = ctx.state.households[id];
    const next = assignment[id] ?? null;
    if (current === undefined) continue;
    if (current.buildingId !== next) {
      const h = ctx.draft.households[id];
      if (h !== undefined) h.buildingId = next;
    }
    if (next === null) rough += current.memberIds.length;
  }
  ctx.report.current.crowding = crowding;
  const crowded = Object.keys(crowding).length;
  if (rough > 0) ctx.report.add({ kind: 'warning', code: 'sleeping-rough', amount: rough });
  if (crowded > 0) ctx.report.add({ kind: 'warning', code: 'overcrowded', amount: crowded });
}

export const decidePhase: Phase = {
  id: 'decide',
  reads: ['decisionLog', 'people', 'policies', 'buildQueue'],
  writes: [
    'people',
    'policies',
    'buildQueue',
    'rationOverrides',
    'toolOverrides',
    'triageOverrides',
    'savedPriorityOrders',
    'myHousehold',
    'promptAnswers',
    'stocks',
    'nextIds',
    'households',
    'currentReport',
  ],
  projection: 'run',
  run(ctx) {
    const decision = ctx.draft.decisionLog[ctx.draft.decisionLog.length - 1];
    if (decision !== undefined) applyDecision(ctx, decision);
    // Standing allocations drawn at the start of the season, after this season's decision:
    // beds by the housing policy (Section 9) and tools by the tool policy (Section 10).
    house(ctx);
    allocateTools(ctx);
    return 'done';
  },
};
